<template>
  <vx-card class="mt-5" no-shadow card-border>
    <div
      class="flex justify-between items-center cursor-pointer select-none"
      @click="togglePanel"
    >
      <h4 class="card-title mb-0">
        {{ sectionTitle }}
        <span v-if="loaded && submissions.length" class="text-sm text-gray-500 font-normal ml-2">
          ({{ submissions.length }})
        </span>
      </h4>
      <feather-icon
        :icon="panelOpen ? 'ChevronUpIcon' : 'ChevronDownIcon'"
        svgClasses="w-5 h-5 stroke-current text-gray-600"
      />
    </div>

    <div v-show="panelOpen" class="mt-4">
      <div v-if="loading" class="flex justify-center py-6">
        <vs-spinner color="primary" />
      </div>

      <div v-else-if="loaded && submissions.length === 0" class="text-sm text-gray-500 py-4">
        {{ $t('dashboard.noDataAvailable') }}
      </div>

      <vs-table
        v-else-if="loaded"
        stripe
        :data="submissions"
        :noDataText="$t('dashboard.noDataAvailable')"
      >
        <template slot="thead">
          <vs-th>{{ $t('dashboard.createdAt') }}</vs-th>
          <vs-th v-if="isSgPeppol">{{ $t('dashboard.transactionId') }}</vs-th>
          <vs-th v-else>{{ $t('dashboard.submissionId') }}</vs-th>
          <vs-th>{{ $t('dashboard.status') }}</vs-th>
          <vs-th v-if="isSgPeppol">{{ $t('dashboard.channel') }}</vs-th>
          <vs-th v-if="isSgPeppol">{{ $t('dashboard.acknowledgementId') }}</vs-th>
          <vs-th>{{ $t('dashboard.updatedAt') }}</vs-th>
          <vs-th>{{ $t('dashboard.action') }}</vs-th>
        </template>
        <template slot-scope="{ data }">
          <tbody>
            <vs-tr v-for="(row, i) in data" :key="row.uuid || i" :data="row">
              <vs-td>
                <span class="text-sm">{{ formatDate(row.createdAt) }}</span>
              </vs-td>
              <vs-td>
                <span class="text-sm font-medium">{{ row.referenceId || row.uuid }}</span>
              </vs-td>
              <vs-td>
                <vs-chip :color="statusColor(row.status)" class="product-order-status">
                  {{ row.status }}
                </vs-chip>
              </vs-td>
              <vs-td v-if="isSgPeppol">
                <span class="text-sm">{{ formatRoute(row.destination) }}</span>
              </vs-td>
              <vs-td v-if="isSgPeppol">
                <span class="text-sm">{{ row.acknowledgementId || '—' }}</span>
              </vs-td>
              <vs-td>
                <span class="text-sm">{{ formatDate(row.updatedAt) }}</span>
              </vs-td>
              <vs-td>
                <feather-icon
                  icon="EyeIcon"
                  svgClasses="w-5 h-5 hover:text-primary stroke-current cursor-pointer"
                  :title="$t('dashboard.view')"
                  @click.stop="viewSubmission(row)"
                />
              </vs-td>
            </vs-tr>
          </tbody>
        </template>
      </vs-table>
    </div>
  </vx-card>
</template>

<script>
import { routeLabels } from '@/constants/sg-peppol-statuses'

export default {
  name: 'EInvoiceSubmissionsSection',
  props: {
    docNo: { type: String, required: true },
    docType: { type: String, required: true },
    isSgPeppol: { type: Boolean, default: false },
  },
  data() {
    return {
      panelOpen: false,
      loaded: false,
      loading: false,
      submissions: [],
    }
  },
  computed: {
    sectionTitle() {
      return this.isSgPeppol
        ? this.$t('dashboard.sgPeppolSubmissions')
        : this.$t('dashboard.lhdnSubmissions')
    },
  },
  methods: {
    togglePanel() {
      this.panelOpen = !this.panelOpen
      if (this.panelOpen && !this.loaded && !this.loading) {
        this.loadSubmissions()
      }
    },
    async loadSubmissions() {
      if (!this.docNo || !this.docType) return
      this.loading = true
      try {
        const data = await this.$store.dispatch('einvoice/fetchSubmissionsByDocNo', {
          docNo: this.docNo,
          type: this.docType,
        })
        this.submissions = Array.isArray(data?.submissions) ? data.submissions : []
        this.loaded = true
      } catch (e) {
        this.$vs.notify({
          color: 'danger',
          title: 'Error',
          text: e.response?.data?.message || 'Failed to load submissions',
        })
        this.submissions = []
        this.loaded = true
      } finally {
        this.loading = false
      }
    },
    viewSubmission(row) {
      const path = this.isSgPeppol
        ? `/sg-peppol-submissions/${row.uuid}`
        : `/lhdn-submissions/${row.uuid}`
      this.$router.push(path).catch(() => {})
    },
    formatRoute(destination) {
      return destination ? (routeLabels[destination] || destination) : '—'
    },
    formatDate(value) {
      if (!value) return '—'
      return this.$moment(value).format('DD MMM YYYY, hh:mm A')
    },
    statusColor(status) {
      const s = String(status || '').toLowerCase()
      if (['completed', 'accepted', 'valid', 'succ', 'successful'].some(k => s.includes(k))) {
        return 'success'
      }
      if (['error', 'rejected', 'invalid', 'fail', 'failed'].some(k => s.includes(k))) {
        return 'danger'
      }
      if (['held', 'processing', 'submitted', 'partiallycompleted'].some(k => s.includes(k))) {
        return 'warning'
      }
      return 'primary'
    },
  },
}
</script>
